/**
 * MY — Incorporation DOC (M32014 / M32104) chatflow.
 * Preset Studio: title → T-Link incorporation (optional) → confirm + live document fill.
 */
import type { ConversationMessage } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

export const incorporationDocFlow: ChatFlow = {
  id: 'incorporation-doc',
  taskCodeMatch: ['M32014', 'M32104'],
  categoryMatch: ['incorporation doc', 'incorporationdoc', 'incorporation (pre)', 'incorporation pre'],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `Welcome to **Incorporation DOC**.\n\n` +
          `This preset prepares a live document package (${docCount || 3}+ document(s)): ` +
          `Section 14 (Particulars of Company), Section 201 Declaration, and Section 201 with Attestation.\n\n` +
          `Fill the documents on the right. Use **✨** on Company Name / Director / Member to look up contacts.\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: {
        targetVarKey: '_taskTitle',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage:
        'Optionally link a **T-Link / incorporation** for auto-fill, or skip and fill the documents manually.',
      widget: 'incorporation-doc-entity',
      widgetConfig: {
        targetVarKey: '_tlinkEntity',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Ready. Complete Section 14 and Section 201 on the right — add directors/members as needed. ' +
        'Click **Generate** when ready.',
      widget: null,
      nextStep: null,
    },
    {
      id: 'officer-role',
      aiMessage: 'Please select a role for the person you want to look up.',
      widget: 'officer-role-buttons',
      awaitEvent: true,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a person from the list.',
      widget: 'officer-list',
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage: 'Person details loaded. The form has been pre-filled.',
      widget: 'officer-field-picker',
      awaitEvent: true,
    },
    {
      id: 'address-input',
      aiMessage: 'Enter the address below.',
      widget: 'address-input',
      awaitEvent: true,
    },
    {
      id: 'pick-msic',
      aiMessage: 'Search and select an **MSIC / Business Nature**.',
      widget: 'business-code',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (
      (eventName === 'value-submit' || eventName === 'text-input-submit') &&
      context.currentStepId === 'input-title'
    ) {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { _taskTitle: String(title).trim(), taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    // Skip T-Link linking (same as incorporationFlow) — Step2 또는 문서 ✨ 재오픈
    if (
      (eventName === 'value-submit' ||
        eventName === 'skip' ||
        eventName === 'entity-skip') &&
      (context.currentStepId === 'select-entity' || context.currentStepId === 'pick-company')
    ) {
      // empty value-submit from Skip button
      // 회사 링크를 해제 → 우측 패널이 자동 채운 값을 초기화하도록 entity/회사명 formData 를 비운다.
      return {
        messages: [
          {
            role: 'user',
            content: 'Skipped incorporation linking. Fill documents manually.',
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd: '',
          icpCd: '',
          entityName: '',
          company_name: '',
          registrationNo: '',
          tlinkData: null,
          entitySkipped: true,
        },
        nextStepId: 'confirm',
      };
    }

    if (
      (eventName === 'setup-entity-autofill' || eventName === 'entity-field-select')
    ) {
      let parsed: any = payload;
      if (typeof payload === 'string') {
        try {
          parsed = JSON.parse(payload);
        } catch {
          parsed = { _entityName: payload };
        }
      }
      const entityCd = String(
        parsed?._icpCd || parsed?.icpCd || parsed?.entityCd || '',
      ).trim();
      const entityName = String(
        parsed?._entityName ||
          parsed?.entityName ||
          parsed?.companyName ||
          'Entity',
      ).trim();
      // incorporation 카드 선택만 처리 (officer / address entity-field-select 와 구분)
      const isIncPick = !!(parsed?._icpCd || parsed?.icpCd);
      if (eventName === 'entity-field-select' && !isIncPick) return {};
      if (!entityCd) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Linked incorporation: **${entityName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd,
          icpCd: entityCd,
          entityName,
          company_name: entityName,
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const key = String(ctx.key || '');
      // Business Nature / MSIC ✨ → MSIC 검색 위젯
      if (
        ctx.lookupType === 'msic' ||
        /^business_nature_\d+$/.test(key) ||
        /^msic_code_\d+$/.test(key)
      ) {
        const slot =
          String(ctx.msicSlot || key.match(/(\d+)$/)?.[1] || '1').trim() || '1';
        const natureKey = `business_nature_${slot}`;
        return {
          messages: [
            {
              role: 'user',
              content: `Look up **Business Nature ${slot}** (MSIC)…`,
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          formUpdates: { _msicTargetKey: natureKey, _msicSlot: slot },
          nextStepId: 'pick-msic',
          nextStepWidgetData: { targetVarKey: natureKey },
        };
      }
      // Proposed company name ✨ → incorporation 테이블 회사 목록
      if (
        key === 'company_name' ||
        ctx.lookupType === 'incorporation' ||
        (ctx.fieldGroup === 'incorporation-doc' && !ctx.defaultRole)
      ) {
        return {
          messages: [
            {
              role: 'user',
              content: 'Select a **company** from incorporations…',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'select-entity',
        };
      }
      const entityCd = context.entityCd || context.formData.entityCd || context.formData.icpCd || '';
      // preloadedOfficers(예: incorporation-pre CDD 의 T-Link 인물) 가 있으면 entityCd 없어도 진행.
      const preloadedOfficers = Array.isArray(ctx.preloadedOfficers) ? ctx.preloadedOfficers : null;
      if (!entityCd && !(preloadedOfficers && preloadedOfficers.length)) {
        return {
          messages: [
            {
              role: 'ai',
              content:
                'Link a company first (✨ on **Proposed company name**), or type the name directly on the document.',
              timestamp: Date.now(),
              stepId: 1,
              source: 'system',
            },
          ],
        };
      }
      const role = preloadedOfficers ? 'ENTITY_OFFICER' : (ctx.defaultRole || 'DIRECTOR');
      return {
        messages: [
          {
            role: 'user',
            content: preloadedOfficers ? 'Select a person…' : `Looking up **${role}**…`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role, preloadedOfficers: preloadedOfficers || undefined },
      };
    }

    // MSIC / Business Nature 선택
    if (eventName === 'value-submit' && context.currentStepId === 'pick-msic') {
      let code = '';
      let name = '';
      if (payload && typeof payload === 'object') {
        code = String(payload.code || '').trim();
        name = String(payload.name || payload.value || '').trim();
      } else {
        name = String(payload || '').trim();
      }
      if (!name && !code) return {};
      const slot =
        String(context.formData._msicSlot || '').trim() ||
        String(context.formData._msicTargetKey || '').match(/(\d+)$/)?.[1] ||
        '1';
      const natureKey = `business_nature_${slot}`;
      const codeKey = `msic_code_${slot}`;
      const label = name || code;
      return {
        messages: [
          {
            role: 'user',
            content: `Business Nature ${slot}: **${label}**${code ? ` (${code})` : ''}`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          [natureKey]: name || code,
          [codeKey]: code || name,
          _msicTargetKey: '',
          _msicSlot: '',
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'officer-role-select' && context.currentStepId === 'officer-role') {
      const role = payload;
      const entityCd = context.entityCd || context.formData.entityCd || '';
      return {
        messages: [{ role: 'user', content: `Role: **${role}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role },
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected person';
      return {
        messages: [
          {
            role: 'user',
            content: `Selected person: **${officerName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-detail',
      };
    }

    if (eventName === 'officer-list-back' && context.currentStepId === 'officer-list') {
      return { nextStepId: 'confirm' };
    }

    if (eventName === 'request-address-input') {
      return {
        messages: [
          { role: 'user', content: 'Enter an address.', timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'address-input',
      };
    }

    return {};
  },
};
